import React from 'react';

import { Formik } from 'formik';

import { observer } from 'mobx-react';

import { Button } from 'components/Button';

import { SectionGrid } from 'components/BackgroundInformation/styles';

import { useStore } from 'utils/hooks/store';
import { UuidableName, LinksType, AuditTaskLinkExtraData } from 'utils/api/types';
import {
  InspectionTaskLinkType,
  InspectionTaskLinkToUpdateType,
  InspectionEntity,
} from 'utils/api/modules/workflows/workflows.types';
import { AuditLinkEntity } from 'utils/api/modules/entities/entities.types';

import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';

import CustomDatePicker from 'components/SharedFormComponents/CustomDatePicker';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';

import TaskStatusSelect from 'components/TaskStatusSelect';

import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField';

import EntitySearchField from 'components/EntitySearchField';

import { useEntityDisplayValue } from 'components/Entities/EntityDisplayValue/utils';

import { EntityModules, isRegisterEntityModule } from 'core/constants';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import { EditTaskRowName } from './styles';
import InspectionField from '../components/InspectionsField';
import QuestionField from '../components/QuestionField/QuestionField';

const TASK_LINKABLE_AUDITS = { task_linkable_only: true };

const Row: React.FC<{ name: string; children: React.ReactNode; id?: string }> = ({
  name,
  children,
  id,
}): React.ReactElement => (
  <div key={id}>
    <EditTaskRowName>{name}</EditTaskRowName>
    <div>{children}</div>
  </div>
);

const EditTaskProperties: React.FunctionComponent<{
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setEditing }): React.ReactElement => {
  const {
    taskDetails: { details: detailsRaw },
    taskDetails,
  } = useStore();
  const details = detailsRaw as TaskListResponse;

  const { session } = useStore();

  const auditsEnabled = !!session.user?.activeEntityModules.includes(EntityModules.Audits);
  const savedAuditLink = details.links?.find((l) => l.entity === AuditLinkEntity);
  const savedInspectionLink = details.links?.find((l) => l.entity === InspectionEntity);

  const onUpdate = async (
    values: TaskListResponse,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ): Promise<void> => {
    setSubmitting(true);
    const getUuidFromLink = (entity: string): string | null => {
      const link = values.links?.find((l) => l.entity === entity);
      return link ? link.instance.uuid : null;
    };

    let linksToUpdate: InspectionTaskLinkToUpdateType[] = [];

    // Get relevant UUIDs from links
    const entityModulesForTask = [
      EntityModules.Sites,
      EntityModules.Assets,
      EntityModules.Contacts,
      EntityModules.RisksAssessments,
      EntityModules.Risks,
      EntityModules.Events,
      EntityModules.Documents,
    ];

    const updatedTaskLinks = entityModulesForTask.reduce<Record<string, string | null>>((acc, module) => {
      const key = getEntityTranslation[module].singularKey;
      const uuid = getUuidFromLink(module);
      acc[key] = uuid ?? null;
      return acc;
    }, {});

    if (values.links && values.links.length > 0) {
      const inspectionLink = values.links.find((l) => l.entity === InspectionEntity);
      if (inspectionLink && inspectionLink.extraData) {
        linksToUpdate = [
          {
            entity: InspectionEntity,
            instance: inspectionLink.instance.uuid,
            extraData: {
              questionId: (inspectionLink.extraData as InspectionTaskLinkType).questionId,
            },
          },
        ];
      } else if (inspectionLink && inspectionLink.instance === null) {
        linksToUpdate = linksToUpdate.filter((link) => link.entity !== InspectionEntity);
      } else if (inspectionLink) {
        linksToUpdate = [
          {
            entity: InspectionEntity,
            instance: (inspectionLink && inspectionLink.instance.uuid) || '',
            extraData: {},
          },
        ];
      }

      // `links` is a full replacement, so the audit link goes back on every save. Re-picking the
      // saved audit keeps the question the action was raised from.
      const auditLink = values.links.find((l) => l.entity === AuditLinkEntity);
      if (auditLink) {
        const questionSource =
          savedAuditLink && auditLink.instance.uuid === savedAuditLink.instance.uuid ? savedAuditLink : auditLink;
        const questionId = (questionSource.extraData as AuditTaskLinkExtraData | undefined)?.questionId;
        linksToUpdate = [
          ...linksToUpdate,
          {
            entity: AuditLinkEntity,
            instance: auditLink.instance.uuid,
            extraData: questionId ? { questionId } : {},
          },
        ];
      }
    }

    const updatedData = {
      ...values,
      links: linksToUpdate,
      dueDate: values.dueDate || null,
      assigned: values.assigned ? values.assigned.uuid : null,
      ...updatedTaskLinks,
    } as unknown as Partial<TaskListResponse>;

    const res = await taskDetails.updateTask(updatedData);
    setSubmitting(false);
    setEditing(!res);
  };

  const handleUpdateField = (
    field: string,
    value: UuidableName | UuidableName[] | null,
    setFieldValue: (field: string, value: UuidableName | UuidableName[] | null | LinksType[]) => void,
    values: TaskListResponse
  ): void => {
    if (!Array.isArray(value)) {
      if (!value) {
        const links = values.links.filter((link) => link.entity !== field);
        setFieldValue('links', links);
        return;
      }
      const links = values.links.map((link) => {
        if (link.entity === field) {
          return {
            entity: field,
            instance: { name: value.name, uuid: value.uuid },
          };
        }
        return link;
      });
      if (links.every((link) => link.entity !== field)) {
        links.push({
          entity: field,
          instance: { name: value.name, uuid: value.uuid },
        });
      }
      setFieldValue('links', links);
    }
  };

  // for scheduled tasks, remove status field and disable form and question fields
  const isScheduledTask = details.template !== null;

  return (
    <Formik initialValues={details} onSubmit={onUpdate}>
      {({ values, setFieldValue, isSubmitting, handleSubmit }) => {
        // oxlint-disable-next-line typescript/no-explicit-any
        const updateField = (field: string, value: any): void => {
          if (field === 'links') {
            const links = values.links.map((link) => {
              if (link.entity === value.entity) {
                return value;
              }
              return link;
            });
            // if there was no link with the entity, add it
            if (links.every((link) => link.entity !== value.entity)) {
              links.push(value);
            }
            setFieldValue('links', links);
          } else {
            setFieldValue(field, value);
          }
        };

        // One audit per action, so a pick or a clear also replaces an old Form link.
        const updateAuditField = (value: UuidableName | UuidableName[] | null): void => {
          const links = values.links.filter(
            (link) => link.entity !== AuditLinkEntity && link.entity !== InspectionEntity
          );
          if (value && !Array.isArray(value)) {
            links.push({ entity: AuditLinkEntity, instance: { name: value.name, uuid: value.uuid } });
          }
          setFieldValue('links', links);
        };

        return (
          <>
            <Row name="Name" id="field-1">
              <CustomTextInputField
                description="Name"
                value={values.name}
                onChange={(value) => setFieldValue('name', value)}
              />
            </Row>
            {auditsEnabled && !isScheduledTask && (
              <Row name="Audit" id="field-audit">
                <EntitySearchField
                  field={AuditLinkEntity}
                  module={EntityModules.Audits}
                  data={(savedAuditLink ?? savedInspectionLink)?.instance as UuidableName}
                  extraParams={TASK_LINKABLE_AUDITS}
                  updateField={(_, value) => updateAuditField(value)}
                />
              </Row>
            )}
            {!auditsEnabled && session.user?.licenses.workflows && !isScheduledTask && (
              <InspectionField updateField={updateField} taskDetailsData={values} />
            )}
            {!auditsEnabled && session.user?.licenses.workflows && !isScheduledTask && (
              <QuestionField updateField={updateField} taskDetailsData={values} />
            )}

            {session.user?.activeEntityModules.filter(isRegisterEntityModule).map((module) => {
              const { entityLink, singular } = useEntityDisplayValue(module, details.links);

              return (
                <Row name={singular} id={`related-${singular}-edit-label`} key={`related-${singular}-edit-label`}>
                  <EntitySearchField
                    field={module}
                    module={module}
                    data={entityLink?.instance as UuidableName}
                    updateField={(field, value) => handleUpdateField(field, value, setFieldValue, values)}
                  />
                </Row>
              );
            })}

            <Row name="Description" id="field-3">
              <CustomTextInputField
                description="Description"
                value={values.description}
                onChange={(value) => setFieldValue('description', value)}
                multiline
              />
            </Row>

            <Row name="Assigned to" id="field-4">
              <CustomUserDropdownField
                description=""
                value={values.assigned ? { uuid: values.assigned.uuid, name: values.assigned.name } : null}
                onChange={(value) => {
                  setFieldValue('assigned', value || null);
                }}
              />
            </Row>

            {/* Only show status field if template is null */}
            {!isScheduledTask && (
              <Row id="field-5" name="Status">
                <TaskStatusSelect
                  value={values.status}
                  onChange={(value) => {
                    setFieldValue('status', value);
                  }}
                />
              </Row>
            )}
            <Row name="Due date" id="field-6">
              <CustomDatePicker
                tzFree
                value={values.dueDate || undefined}
                onChange={(val) => setFieldValue('dueDate', val ?? '')}
                description="Due date"
                size="medium"
              />
            </Row>

            <SectionGrid item container xs={12} marginTop="20px" justifyContent="end" marginBottom="20px">
              <Button
                disabled={isSubmitting}
                onClick={() => setEditing(false)}
                style={{
                  marginRight: '16px',
                }}
                variant="outlined"
                size="large"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                size="large"
                onClick={() => handleSubmit()}
              >
                Save
              </Button>
            </SectionGrid>
          </>
        );
      }}
    </Formik>
  );
};

export default observer(EditTaskProperties);
